import {
  SearchOutlined,
  FilterListOutlined,
  ViewWeekOutlined,
  ArrowDownwardOutlined,
} from "@mui/icons-material";
import { colorsFoundation } from "../../theme/tokens/colors-foundation.js";
import componentTokens from "../../theme/tokens/components.js";
import { dimensions } from "../../theme/tokens/dimensions.js";
import { typographyTokens } from "../../theme/tokens/typography.js";
import { hexToRgba } from "../../theme/utils/colors.js";

// Shared cool-grey table styling from the click-dummy. This is the single home
// for the ~150 lines of MRT styling that the Schäden page used to inline, so
// every header-controls table (index pages that render search/filter/column
// controls in the page header via `useMaterialReactTable`) looks identical.
// The DataTable fleet draws the same values from componentTokens.dataTable.
const dt = componentTokens.dataTable;
const ACCENT = colorsFoundation.key.blue[600]; // primary role (rose in generic skin)

/**
 * buildTablePreset — spread the result into a `useMaterialReactTable` config to
 * adopt the dummy look. Pair with `buildRowClickProps` for clickable rows and
 * `buildToolbarControlSx` for the MRT_* control buttons in the page header.
 */
export function buildTablePreset() {
  return {
    icons: {
      SearchIcon: SearchOutlined,
      FilterListIcon: FilterListOutlined,
      ViewColumnIcon: ViewWeekOutlined,
      ArrowDownwardIcon: ArrowDownwardOutlined,
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${dt.paper.borderColor}`,
        borderRadius: dt.paper.borderRadius,
        overflow: "hidden",
        boxShadow: "none",
      },
    },
    muiTableContainerProps: { sx: { overflowX: "auto" } },
    muiTableProps: { sx: { tableLayout: "fixed", width: "100%" } },
    muiTableHeadProps: { sx: { "& tr": { boxShadow: "none" } } },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: dt.headCell.background,
        borderBottom: `1px solid ${dt.headCell.borderColor}`,
        boxShadow: "none",
        color: dt.headCell.color,
        fontSize: dt.headCell.fontSize,
        fontWeight: dt.headCell.fontWeight,
        // 40px header row to match the click dummy (was ~50px at comfortable density).
        height: "40px",
        paddingTop: dimensions.sm,
        paddingBottom: dimensions.sm,
        verticalAlign: "middle",
        "&:first-of-type": { pl: dimensions["2xl"] },
        "&:last-of-type": { pr: dimensions["2xl"] },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          fontWeight: dt.headCell.fontWeight,
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderColor: dt.bodyCell.borderColor,
        color: "text.primary",
        fontSize: dt.bodyCell.fontSize,
        py: dimensions.lg,
        "&:first-of-type": { pl: dimensions["2xl"] },
        "&:last-of-type": { pr: dimensions["2xl"] },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        boxShadow: "none",
        borderTop: `1px solid ${dt.paper.borderColor}`,
        minHeight: "3.25rem",
        "& .MuiTablePagination-root": { color: colorsFoundation.gray[500] },
        "& .MuiTablePagination-root, & .MuiTablePagination-root *": {
          fontFamily: `${typographyTokens.family.body} !important`,
          fontSize: "12px !important",
        },
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 25, 50],
      showFirstButton: false,
      showLastButton: false,
    },
    muiSearchTextFieldProps: {
      placeholder: "Suche",
      variant: "outlined",
      size: "small",
      sx: {
        width: dt.search.width,
        maxWidth: "100%",
        // Dummy: 17px magnifier in a light gray-400, not MUI's default ~24px/darker.
        // MRT renders the leading SearchIcon as a bare svg (direct child of the
        // input root), NOT inside an InputAdornment — so target it directly.
        "& .MuiOutlinedInput-root > .MuiSvgIcon-root": {
          fontSize: "17px",
          color: colorsFoundation.gray[400],
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: dt.search.borderRadius,
          backgroundColor: colorsFoundation.neutral.white,
          fontSize: "14px",
          "& fieldset": {
            borderColor: dt.paper.borderColor,
            borderWidth: "1.5px",
          },
          "&:hover fieldset": { borderColor: dt.paper.borderColor },
          "&.Mui-focused fieldset": {
            borderColor: ACCENT,
            borderWidth: "1.5px",
            boxShadow: `0 0 0 3px ${hexToRgba(ACCENT, 0.1)}`,
          },
        },
      },
    },
  };
}

/**
 * buildRowClickProps — pass a handler that receives the MRT row and returns
 * `muiTableBodyRowProps` for clickable rows with the dummy hover treatment.
 * Example: `muiTableBodyRowProps: buildRowClickProps((row) => navigate(...))`.
 */
export function buildRowClickProps(onRowClick) {
  return ({ row }) => ({
    onClick: () => onRowClick(row),
    sx: {
      cursor: "pointer",
      "&:hover td": { backgroundColor: dt.row.hoverBackground },
    },
  });
}

/**
 * buildToolbarControlSx — sx for the MRT_ToggleFiltersButton /
 * MRT_ShowHideColumnsButton controls rendered in the page header.
 */
export function buildToolbarControlSx() {
  return {
    width: dt.toolbarControl.size,
    height: dt.toolbarControl.size,
    borderRadius: dt.toolbarControl.borderRadius,
    color: dt.toolbarControl.color,
    // Dummy: 17px line icons. MUI's default IconButton glyph is ~20-24px and the
    // filled-outline style reads heavier, so pin it to 17px to match.
    "& .MuiSvgIcon-root": { fontSize: "17px" },
    "&:hover": {
      backgroundColor: dt.toolbarControl.hoverBg,
      color: dt.toolbarControl.hoverColor,
    },
  };
}
